import { weakMemoize } from '@renderer/utils/weak-memoize'

/**
 * The reminder wire/display shapes and the mapping between them, kept in a leaf
 * module so consumers that only need to map (the meeting view's For You
 * section, the reminder toast) don't pull in `use-reminders`' React Query,
 * auth, and gateway dependencies — and so the mapping stays unit-testable.
 * `use-reminders` re-exports all of it, so importing from either is fine.
 */

/** Lightweight pointer to the context that produced the reminder. */
export interface ReminderSource {
  type: 'audioNote' | 'dailySummary' | 'insight' | 'conversation' | 'screen'
  id: string
  title?: string
}

/** Shape returned by the reminders API. */
export interface ReminderApiItem {
  id: string
  text: string
  description?: string | null
  userDescription?: string | null
  dueAt: string | null
  type: 'note' | 'task'
  source: ReminderSource | null
  createdAt: string
  updatedAt: string
  resolvedAt: string | null
  attachments: Array<string>
  jobId: string | null
  metadata: Record<string, string> | null
  priority?: boolean
  manualPriority?: boolean | null
  triggeringLlmMessageId?: string | null
  conversationId?: string | null
  feedback?: { rating?: number } | null
  /** The transcript lines that raised it. Only a single reminder (`GET /v1/reminders/:id`) carries them. */
  explanation?: string | null
}

/**
 * Highlight resolved the reminder on its own, from what it saw on the user's
 * screen. `evidence` is the line that showed the work was done.
 */
export interface ReminderAutoResolution {
  evidence: string
  at: string
}

/** The server writes `metadata.autoResolved` as an object, so read it past the string-valued type. */
export function autoResolutionOf(
  item: Pick<ReminderApiItem, 'metadata' | 'resolvedAt'>,
): ReminderAutoResolution | null {
  if (!item.resolvedAt) return null
  const raw = (item.metadata as Record<string, unknown> | null)?.autoResolved as
    | { evidence?: unknown; at?: unknown }
    | undefined
  if (typeof raw?.evidence !== 'string' || typeof raw.at !== 'string') return null
  return { evidence: raw.evidence, at: raw.at }
}

/** Display-friendly shape consumed by UI components. */
export type ReminderItemState = 'default' | 'new' | 'dismissed'
export type ReminderItemType = 'task' | 'note'

export interface ReminderItem {
  id: string
  title: string
  /** The longer detail under the title, when whoever wrote the reminder gave one. */
  description?: string
  /** The user's own notes, as markdown. Highlight never writes them. */
  userDescription?: string
  type: ReminderItemType
  source: ReminderSource | null
  scheduledAt?: Date
  state: ReminderItemState
  createdAt: Date
  updatedAt: Date
  resolved?: boolean
  attachments?: Array<string>
  priority?: boolean
  manualPriority?: boolean | null
  triggeringLlmMessageId?: string | null
  conversationId?: string | null
  feedback?: { rating?: number } | null
  autoResolved?: ReminderAutoResolution
  /** The meeting recording that raised it, which planner v2 notes: its source names no meeting until the note is saved. */
  audioSessionId?: string
}

export function mapApiItemToReminderItem(item: ReminderApiItem): ReminderItem {
  // State derivation: dismissed > user-created > readAt > new
  let state: ReminderItemState = 'new'
  if (item.metadata?.dismissed === 'true') {
    state = 'dismissed'
  } else if (!item.source || item.metadata?.readAt) {
    state = 'default'
  }

  const autoResolution = autoResolutionOf(item)
  const description = item.description?.trim()

  return {
    id: item.id,
    title: item.text,
    ...(description && { description }),
    ...(item.userDescription && { userDescription: item.userDescription }),
    type: item.type === 'task' ? 'task' : 'note',
    source: item.source ?? null,
    ...(item.dueAt && { scheduledAt: new Date(item.dueAt) }),
    state,
    createdAt: new Date(item.createdAt),
    updatedAt: new Date(item.updatedAt),
    resolved: !!item.resolvedAt,
    ...(item.attachments.length > 0 && { attachments: item.attachments }),
    ...(item.priority === true && { priority: true }),
    ...(item.manualPriority !== undefined && { manualPriority: item.manualPriority }),
    ...(item.triggeringLlmMessageId != null && { triggeringLlmMessageId: item.triggeringLlmMessageId }),
    ...(item.conversationId != null && { conversationId: item.conversationId }),
    ...(item.feedback != null && { feedback: item.feedback }),
    ...(autoResolution && { autoResolved: autoResolution }),
    ...(typeof item.metadata?.audioSessionId === 'string' && { audioSessionId: item.metadata.audioSessionId }),
  }
}

/** Unresolved, not dismissed, and past due — same count as the reminders section badge. */
export function isOverdueReminder(item: ReminderApiItem, now: number = Date.now()): boolean {
  if (item.metadata?.dismissed === 'true' || item.resolvedAt || !item.dueAt) {
    return false
  }
  const due = new Date(item.dueAt).getTime()
  return Number.isFinite(due) && due < now
}

export function countOverdueReminders(items: Array<ReminderApiItem> | undefined, now: number = Date.now()): number {
  if (!items) {
    return 0
  }
  return items.filter((item) => isOverdueReminder(item, now)).length
}

// Identity-preserving mapper (see weakMemoize): an unchanged apiItem keeps its
// ReminderItem instance across query updates, preserving memoized ReminderCells.
export const toReminderItem = weakMemoize(mapApiItemToReminderItem)

export function toReminderItems(apiItems: Array<ReminderApiItem> | undefined): Array<ReminderItem> {
  return (apiItems ?? []).map(toReminderItem)
}
